import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

/**
 * Calendar-week bucketing for sampler / pipeline jobs.
 *
 * **Timezone policy:** `created_at` strings are parsed with `parseISO` (ISO 8601). Each
 * instant is then placed into a Monday-starting calendar week using **the host’s local
 * timezone**, identical to `date-fns` `startOfWeek` / `endOfWeek` with
 * `{ weekStartsOn: 1 }` (same behavior as `WeekSelector` in the browser). Server-side
 * callers in UTC will therefore bucket by UTC local calendar; clients bucket by the
 * user’s locale. Use explicit offset/Zulu timestamps in `created_at` when the instant
 * must be unambiguous; week boundaries still follow local wall time after parsing.
 *
 * **UI:** Range pills and tooltips in `@/components/shared/WeekSelector` use `date-fns`
 * `format()` in the same local timezone as this module’s bucketing.
 */

export interface WeekJobRecord {
  id: string;
  created_at: string;
}

export type PeriodGranularity = 'week' | 'month';

export interface PeriodJobBucketBase {
  grain: PeriodGranularity;
  /** Stable key used for URL/deep-link round-tripping. */
  periodKey: string;
  periodStart: Date;
  periodEnd: Date;
  /** Job IDs in this period, newest `created_at` first. */
  jobIds: string[];
  /** Compact label for pills. */
  label: string;
  /** Expanded label for tooltips. */
  fullLabel: string;
}

export interface WeekJobBucket {
  /** Monday of the week in local time, as `yyyy-MM-dd` — stable for deduping and maps. */
  weekKey: string;
  weekStart: Date;
  weekEnd: Date;
  grain: 'week';
  periodKey: string;
  periodStart: Date;
  periodEnd: Date;
  jobIds: string[];
  label: string;
  fullLabel: string;
}

export interface MonthJobBucket {
  /** Month key in local time, as `yyyy-MM` (e.g. "2026-04"). */
  monthKey: string;
  monthStart: Date;
  monthEnd: Date;
  grain: 'month';
  periodKey: string;
  periodStart: Date;
  periodEnd: Date;
  jobIds: string[];
  label: string;
  fullLabel: string;
}

/** Stable week key: ISO calendar date (`yyyy-MM-dd`) of the Monday that starts the week. */
export function weekKeyFromWeekStart(weekStartMonday: Date): string {
  return format(weekStartMonday, 'yyyy-MM-dd');
}

/** Stable month key (`yyyy-MM`) of the month start. */
export function monthKeyFromMonthStart(monthStart: Date): string {
  return format(monthStart, 'yyyy-MM');
}

function weekBoundsForInstant(instant: Date): { weekStart: Date; weekEnd: Date } {
  return {
    weekStart: startOfWeek(instant, { weekStartsOn: 1 }),
    weekEnd: endOfWeek(instant, { weekStartsOn: 1 }),
  };
}

function monthBoundsForInstant(instant: Date): { monthStart: Date; monthEnd: Date } {
  const monthStart = startOfMonth(instant);
  return {
    monthStart,
    monthEnd: addMonths(monthStart, 1),
  };
}

/**
 * Groups jobs into Monday-start weeks. Buckets are ordered newest week first.
 * Within a bucket, `jobIds` are sorted by `created_at` descending.
 */
export function groupJobsByWeek(jobs: readonly WeekJobRecord[]): WeekJobBucket[] {
  if (jobs.length === 0) return [];

  const byKey = new Map<
    string,
    { weekStart: Date; weekEnd: Date; entries: WeekJobRecord[] }
  >();

  for (const job of jobs) {
    const instant = parseISO(job.created_at);
    const { weekStart, weekEnd } = weekBoundsForInstant(instant);
    const key = weekKeyFromWeekStart(weekStart);
    const existing = byKey.get(key);
    if (existing) {
      existing.entries.push(job);
    } else {
      byKey.set(key, { weekStart, weekEnd, entries: [job] });
    }
  }

  const buckets: WeekJobBucket[] = [];

  for (const { weekStart, weekEnd, entries } of byKey.values()) {
    const sorted = [...entries].sort(
      (a, b) => parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime(),
    );
    const weekKey = weekKeyFromWeekStart(weekStart);
    buckets.push({
      weekKey,
      weekStart,
      weekEnd,
      grain: 'week',
      periodKey: weekKey,
      periodStart: weekStart,
      periodEnd: weekEnd,
      jobIds: sorted.map((j) => j.id),
      label: format(weekStart, 'MMM d'),
      fullLabel: `Week of ${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`,
    });
  }

  buckets.sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
  return buckets;
}

/** Groups jobs into calendar months. Buckets are ordered newest month first. */
export function groupJobsByMonth(jobs: readonly WeekJobRecord[]): MonthJobBucket[] {
  if (jobs.length === 0) return [];

  const byKey = new Map<
    string,
    { monthStart: Date; monthEnd: Date; entries: WeekJobRecord[] }
  >();

  for (const job of jobs) {
    const instant = parseISO(job.created_at);
    const { monthStart, monthEnd } = monthBoundsForInstant(instant);
    const key = monthKeyFromMonthStart(monthStart);
    const existing = byKey.get(key);
    if (existing) {
      existing.entries.push(job);
    } else {
      byKey.set(key, { monthStart, monthEnd, entries: [job] });
    }
  }

  const buckets: MonthJobBucket[] = [];
  for (const { monthStart, monthEnd, entries } of byKey.values()) {
    const sorted = [...entries].sort(
      (a, b) => parseISO(b.created_at).getTime() - parseISO(a.created_at).getTime(),
    );
    const monthKey = monthKeyFromMonthStart(monthStart);
    buckets.push({
      monthKey,
      monthStart,
      monthEnd,
      grain: 'month',
      periodKey: monthKey,
      periodStart: monthStart,
      periodEnd: monthEnd,
      jobIds: sorted.map((j) => j.id),
      label: format(monthStart, 'MMM yyyy'),
      fullLabel: `${format(monthStart, 'MMM d')} - ${format(
        endOfMonth(monthStart),
        'MMM d, yyyy',
      )}`,
    });
  }

  buckets.sort((a, b) => b.monthStart.getTime() - a.monthStart.getTime());
  return buckets;
}

export type PeriodJobBucket = WeekJobBucket | MonthJobBucket;

export function groupJobsByPeriod(
  jobs: readonly WeekJobRecord[],
  grain: PeriodGranularity,
): PeriodJobBucket[] {
  if (grain === 'month') {
    return groupJobsByMonth(jobs);
  }
  return groupJobsByWeek(jobs);
}
